import { randomId } from 'services/compose/utils'
import { AIConversation, AIConversationKind, AIConversationMessage } from './net'

const DEFAULT_RATIO = '1:1'

export interface RawConversationItem {
  sessionId: string | null
  createdAt: string
  prompt: string
  message: string | null
  options: string | null
  error: string | null
  imageUrl: string | null
  summary: string | null
}

export interface RawConversationGroup {
  sessionId: string | null
  summary: string
  createdAt: string | null
  lastUpdatedAt: string | null
  count: number
  items: RawConversationItem[]
}

function parseMessageVariations(raw: string | null): string[] {
  if (!raw) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0)
      }
    } catch {
      // fall through, treat raw as plain text
    }
  }
  return [raw]
}

function parseOptions(raw: string | null): Record<string, any> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function buildMessagesFromItem(item: RawConversationItem, idPrefix: string): AIConversationMessage[] {
  const messages: AIConversationMessage[] = []
  if (item.prompt) {
    messages.push({
      id: `${idPrefix}-u`,
      role: 'user',
      kind: 'text',
      text: item.prompt
    })
  }
  if (item.message) {
    const variations = parseMessageVariations(item.message)
    variations.forEach((text, i) => {
      messages.push({
        id: `${idPrefix}-a-${i}`,
        role: 'assistant',
        kind: 'text',
        text
      })
    })
  }
  if (item.imageUrl) {
    const opts = parseOptions(item.options)
    messages.push({
      id: `${idPrefix}-a-img`,
      role: 'assistant',
      kind: 'image',
      imageUrl: item.imageUrl,
      ratio: typeof opts.aspect_ratio === 'string' ? opts.aspect_ratio : DEFAULT_RATIO
    })
  }
  return messages
}

function inferKind(items: RawConversationItem[]): AIConversationKind {
  return items.some(it => Boolean(it.imageUrl)) ? 'image' : 'text'
}

function adaptGroup(group: RawConversationGroup): AIConversation | null {
  if (!Array.isArray(group.items) || group.items.length === 0) return null
  const id = group.sessionId || randomId()
  const messages: AIConversationMessage[] = []
  group.items.forEach((item, i) => {
    messages.push(...buildMessagesFromItem(item, `${id}-${i}`))
  })
  if (messages.length === 0) return null
  return {
    id,
    kind: inferKind(group.items),
    title: group.summary || '',
    createdAt: group.createdAt || group.items[0]?.createdAt || new Date().toISOString(),
    messages
  }
}

export function adaptAIConversations(raw: unknown): AIConversation[] {
  if (!Array.isArray(raw)) return []
  const conversations: AIConversation[] = []
  raw.forEach((group: RawConversationGroup) => {
    if (!group) return
    const conv = adaptGroup(group)
    if (conv) conversations.push(conv)
  })
  conversations.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
  return conversations
}
